import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, RefreshCw, Building2, Sparkles, Users2, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_COVERAGE_BLOCKS = [
  { key: "day",     label: "Day",     start: "06:00", end: "14:00", min_staff: 1 },
  { key: "evening", label: "Evening", start: "14:00", end: "22:00", min_staff: 1 },
  { key: "night",   label: "Night",   start: "22:00", end: "06:00", min_staff: 1 },
];

function SiteDialog({ open, onOpenChange, record, onSubmit }) {
  const [values, setValues] = useState({ name: "", address: "", manager_name: "", phone: "", notes: "", status: "active", coverage_blocks: DEFAULT_COVERAGE_BLOCKS });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      // Merge existing blocks with the defaults so partial data still yields
      // a complete Day/Evening/Night trio in the editor.
      const existingBlocks = record?.coverage_blocks || [];
      const merged = DEFAULT_COVERAGE_BLOCKS.map((def) => {
        const found = existingBlocks.find((b) => b.key === def.key);
        return found ? { ...def, ...found } : def;
      });
      setValues({
        name: record?.name || "",
        address: record?.address || "",
        manager_name: record?.manager_name || "",
        phone: record?.phone || "",
        notes: record?.notes || "",
        status: record?.status || "active",
        coverage_blocks: merged,
      });
    }
  }, [open, record]);

  const updateBlock = (idx, field, val) => {
    setValues((prev) => ({
      ...prev,
      coverage_blocks: prev.coverage_blocks.map((b, i) => i === idx ? { ...b, [field]: field === "min_staff" ? Math.max(0, Number(val) || 0) : val } : b),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!values.name.trim()) return;
    // Sanity: enforce start !== end so overlap calc always has a window.
    const bad = values.coverage_blocks.find((b) => !b.start || !b.end || b.start === b.end);
    if (bad) { toast.error(`${bad.label}: start and end times must differ`); return; }
    setSaving(true);
    try {
      await onSubmit(values);
      onOpenChange(false);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white" data-testid="site-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{record ? "Edit site" : "New site"}</DialogTitle>
          <DialogDescription className="sr-only">{record ? "Update" : "Create a new"} site (branch or care home).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 py-2 max-h-[75vh] overflow-y-auto">
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-medium">Site Name *</Label>
              <Input value={values.name} onChange={(e) => setValues({ ...values, name: e.target.value })} className="mt-1 h-9" data-testid="site-name" required />
            </div>
            <div>
              <Label className="text-xs font-medium">Address</Label>
              <Textarea rows={2} value={values.address} onChange={(e) => setValues({ ...values, address: e.target.value })} className="mt-1 text-sm" data-testid="site-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium">Manager</Label>
                <Input value={values.manager_name} onChange={(e) => setValues({ ...values, manager_name: e.target.value })} className="mt-1 h-9" data-testid="site-manager" />
              </div>
              <div>
                <Label className="text-xs font-medium">Phone</Label>
                <Input value={values.phone} onChange={(e) => setValues({ ...values, phone: e.target.value })} className="mt-1 h-9" data-testid="site-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium">Notes</Label>
              <Textarea rows={2} value={values.notes} onChange={(e) => setValues({ ...values, notes: e.target.value })} className="mt-1 text-sm" data-testid="site-notes" />
            </div>
          </div>

          {/* Coverage requirements — Day / Evening / Night blocks */}
          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/50" data-testid="site-coverage-editor">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <div>
                <div className="text-sm font-semibold text-slate-800">Coverage Requirements</div>
                <div className="text-[11px] text-slate-500">Set the start, end and minimum staff per block. Overnight (e.g. 22:00–06:00) is supported.</div>
              </div>
            </div>
            <div className="grid grid-cols-[80px_1fr_1fr_100px] gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold px-1 pb-1">
              <div>Block</div><div>Start</div><div>End</div><div>Min staff</div>
            </div>
            <div className="space-y-1.5">
              {values.coverage_blocks.map((b, i) => (
                <div key={b.key} className="grid grid-cols-[80px_1fr_1fr_100px] gap-2 items-center" data-testid={`coverage-row-${b.key}`}>
                  <div className="text-sm font-medium text-slate-700">{b.label}</div>
                  <Input type="time" value={b.start} onChange={(e) => updateBlock(i, "start", e.target.value)} className="h-9 text-sm" data-testid={`coverage-${b.key}-start`} />
                  <Input type="time" value={b.end} onChange={(e) => updateBlock(i, "end", e.target.value)} className="h-9 text-sm" data-testid={`coverage-${b.key}-end`} />
                  <Input type="number" min="0" value={b.min_staff} onChange={(e) => updateBlock(i, "min_staff", e.target.value)} className="h-9 text-sm tabular-nums" data-testid={`coverage-${b.key}-min`} />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="site-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="site-save">{saving ? "Saving…" : record ? "Update" : "Create"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sites() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = () => {
    setLoading(true);
    api.get("/sites")
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const submit = async (values) => {
    try {
      if (editing) await api.put(`/sites/${editing.id}`, values);
      else await api.post("/sites", values);
      toast.success(editing ? "Site updated" : "Site created");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this site? Users must be reassigned first.")) return;
    try {
      await api.delete(`/sites/${id}`);
      toast.success("Site deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="hidden lg:flex h-14 border-b border-slate-200 bg-white px-6 items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">System</div>
          <h1 className="font-heading text-base font-semibold flex items-center gap-2" data-testid="page-title">
            <Building2 className="w-4 h-4" /> Sites
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="h-9 text-slate-700" data-testid="btn-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          {isAdmin && <DemoBundleRestore />}
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-9 bg-sky-700 hover:bg-sky-800" data-testid="btn-new-site">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> New Site
            </Button>
          )}
        </div>
      </div>
      {/* Mobile header — Batch 7D */}
      <div className="lg:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between gap-2 shrink-0">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">System</div>
          <h1 className="font-heading text-base font-semibold flex items-center gap-1.5 truncate" data-testid="page-title-mobile">
            <Building2 className="w-4 h-4 shrink-0" /> Sites
          </h1>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="outline" size="sm" onClick={load} className="h-11 w-11 p-0" data-testid="btn-refresh-mobile" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          {isAdmin && (
            <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="h-11 bg-sky-700 hover:bg-sky-800" data-testid="btn-new-site-mobile">
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        {loading ? (
          <div className="p-8 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center">
            <div className="text-sm text-slate-500 mb-3">No sites yet.</div>
            {isAdmin && <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-empty-new"><Plus className="w-3.5 h-3.5 mr-1.5" /> Add your first site</Button>}
          </div>
        ) : (
          <table className="data-table hidden lg:table" data-testid="sites-table">
            <thead>
              <tr>
                <th className="row-num">#</th>
                <th>Name</th>
                <th>Manager</th>
                <th>Phone</th>
                <th>Address</th>
                <th>Coverage (Day / Evening / Night)</th>
                <th className="w-24 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} data-testid={`site-row-${r.id}`}>
                  <td className="row-num">{i + 1}</td>
                  <td className="font-medium">{r.name}</td>
                  <td>{r.manager_name || <span className="text-slate-300">—</span>}</td>
                  <td>{r.phone || <span className="text-slate-300">—</span>}</td>
                  <td className="text-slate-600 max-w-xs truncate">{r.address || <span className="text-slate-300">—</span>}</td>
                  <td className="text-[11px] text-slate-600" data-testid={`site-coverage-${r.id}`}>
                    {(r.coverage_blocks || []).map((b) => (
                      <div key={b.key} className="tabular-nums whitespace-nowrap">
                        <span className="text-slate-500 uppercase tracking-widest text-[9px] font-semibold mr-1">{b.label?.[0]}</span>
                        {b.start}–{b.end} · min {b.min_staff}
                      </div>
                    ))}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {isAdmin && (
                      <>
                        <button onClick={() => { setEditing(r); setDialogOpen(true); }} className="p-1 text-slate-500 hover:text-sky-700" data-testid={`edit-${r.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(r.id)} className="p-1 text-slate-500 hover:text-red-600 ml-0.5" data-testid={`delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Mobile card list — Batch 7D */}
        {!loading && rows.length > 0 && (
          <ul className="lg:hidden divide-y divide-slate-200 border-t border-slate-200" data-testid="sites-cards">
            {rows.map((r) => (
              <li key={r.id} className="p-3 bg-white" data-testid={`site-card-${r.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 break-words">{r.name}</div>
                    {r.manager_name && <div className="text-[11px] text-slate-600">Manager: {r.manager_name}</div>}
                    {r.phone && <div className="text-[11px] text-slate-500">{r.phone}</div>}
                    {r.address && <div className="text-[11px] text-slate-500 break-words mt-0.5">{r.address}</div>}
                    {(r.coverage_blocks || []).length > 0 && (
                      <div className="text-[10px] text-slate-600 mt-1.5 space-y-0.5" data-testid={`site-card-coverage-${r.id}`}>
                        {(r.coverage_blocks || []).map((b) => (
                          <div key={b.key} className="tabular-nums">
                            <span className="text-slate-500 uppercase tracking-widest text-[9px] font-semibold mr-1">{b.label?.[0]}</span>
                            {b.start}–{b.end} · min {b.min_staff}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-1 shrink-0">
                      <button onClick={() => { setEditing(r); setDialogOpen(true); }} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-sky-700" data-testid={`edit-card-${r.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => del(r.id)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-slate-500 hover:text-red-600" data-testid={`delete-card-${r.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <SiteDialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }} record={editing} onSubmit={submit} />
    </div>
  );
}

function DemoBundleRestore() {
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(null);

  const loadInfo = async () => {
    try {
      const r = await api.get("/admin/demo-bundle/info");
      setInfo(r.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadInfo(); }, []);

  if (!info?.available) return null;

  const restore = async () => {
    if (!window.confirm(`Restore ${info.clients?.length || 0} demo service users and all linked records? This is idempotent — safe to run repeatedly.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/demo-bundle/restore");
      toast.success(`Demo bundle restored — ${r.data.total_inserted} new, ${r.data.total_updated} updated`);
      await loadInfo();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const allPresent = info.clients?.every((c) => c.present);
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={restore}
      disabled={busy}
      className="h-9 text-slate-700 border-emerald-300"
      data-testid="btn-restore-demo-bundle"
      title={info.clients?.map((c) => `${c.full_name}${c.present ? " ✓" : ""}`).join(" · ")}
    >
      <Users2 className="w-3.5 h-3.5 mr-1.5 text-emerald-700" />
      {busy ? "Restoring…" : allPresent ? "Reload Demo Data" : "Load Demo Data"}
    </Button>
  );
}
